/**
 * Mix operacional da simulação de presença no turno.
 * - pctRfid: entram com tag RFID na portaria (e tendem a estar presentes na chamada)
 * - pctSemRfid: presentes na chamada sem leitura RFID
 * - pctAusente: não vieram (resto = 100 - rfid - semRfid, se não informado)
 */
export type SimPresenceMix = {
  pctRfid: number;
  pctSemRfid: number;
  pctAusente: number;
};

export const DEFAULT_SIM_PRESENCE_MIX: SimPresenceMix = {
  pctRfid: 90,
  pctSemRfid: 5,
  pctAusente: 5,
};

const STORAGE_KEY = 'ac_sim_presence_mix';

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** Normaliza para somar 100 (ajusta ausentes se necessário). */
export function normalizePresenceMix(partial: Partial<SimPresenceMix>): SimPresenceMix {
  const pctRfid = clampPct(partial.pctRfid ?? DEFAULT_SIM_PRESENCE_MIX.pctRfid);
  const pctSemRfid = clampPct(partial.pctSemRfid ?? DEFAULT_SIM_PRESENCE_MIX.pctSemRfid);
  const pctAusente =
    partial.pctAusente != null
      ? clampPct(partial.pctAusente)
      : clampPct(100 - pctRfid - pctSemRfid);

  const sum = pctRfid + pctSemRfid + pctAusente;
  if (sum === 100) return { pctRfid, pctSemRfid, pctAusente };
  if (sum <= 0) return { ...DEFAULT_SIM_PRESENCE_MIX };

  // Redistribui proporcionalmente
  const f = 100 / sum;
  const r = clampPct(pctRfid * f);
  const s = clampPct(pctSemRfid * f);
  return { pctRfid: r, pctSemRfid: s, pctAusente: clampPct(100 - r - s) };
}

export function loadPresenceMix(): SimPresenceMix {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SIM_PRESENCE_MIX };
    const parsed = JSON.parse(raw) as Partial<SimPresenceMix>;
    return normalizePresenceMix(parsed);
  } catch {
    return { ...DEFAULT_SIM_PRESENCE_MIX };
  }
}

export function savePresenceMix(mix: SimPresenceMix): void {
  const n = normalizePresenceMix(mix);
  const json = JSON.stringify(n);
  try {
    sessionStorage.setItem(STORAGE_KEY, json);
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
}

export function countByPct(total: number, pct: number): number {
  if (total <= 0) return 0;
  return Math.min(total, Math.max(0, Math.round((total * pct) / 100)));
}

/** Embaralha cópia (Fisher–Yates). */
export function shuffleCopy<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
